/**
 * @module camera
 * Manages the front-facing camera stream via getUserMedia.
 *
 * The video feed is mirrored horizontally so user movements feel natural
 * (moving right moves the pointer right on screen). Callers attach the
 * returned stream to a <video> element; the CameraError class signals
 * permission or hardware failures for UI error-state handling.
 *
 * Security note (T-01): camera access is only requested after the user has
 * seen the privacy disclosure on the start screen (STRINGS.camera.privacyNotice).
 */

import { CONFIG } from './game-config.js';

/**
 * Typed error thrown (and caught) by startCamera.
 * The `reason` field drives the error-state UI in StartScreen.
 */
export class CameraError extends Error {
  constructor(
    public readonly reason: 'PERMISSION_DENIED' | 'NO_CAMERA' | 'CONSTRAINT_ERROR',
    message: string,
  ) {
    super(message);
    this.name = 'CameraError';
  }
}

/**
 * Requests the front-facing camera stream with the spec constraints,
 * attaches it to `videoEl`, waits for `canplay`, and resolves.
 *
 * Throws {@link CameraError} on:
 *  - Permission denied (`PERMISSION_DENIED`)
 *  - No suitable camera found (`NO_CAMERA`)
 *  - Constraint not satisfiable (`CONSTRAINT_ERROR`)
 *
 * The returned MediaStream is attached to `videoEl.srcObject`.
 * Callers should call {@link stopCamera} with the stream when done.
 */
export async function startCamera(videoEl: HTMLVideoElement): Promise<MediaStream> {
  let stream: MediaStream;

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 },
        // 60fps where the hardware supports it: halves per-frame motion blur
        // on fast hand swipes (the main cause of tracking dropouts). `ideal`
        // degrades gracefully to 30fps cameras — never OverconstrainedError.
        frameRate: { ideal: CONFIG.tracking.cameraFrameRateIdeal },
      },
    });
  } catch (err: unknown) {
    if (err instanceof DOMException) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        throw new CameraError('PERMISSION_DENIED', 'Camera permission was denied by the user.');
      }
      if (
        err.name === 'NotFoundError' ||
        err.name === 'DevicesNotFoundError' ||
        err.name === 'NotReadableError'
      ) {
        throw new CameraError('NO_CAMERA', 'No suitable camera device was found.');
      }
      if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        throw new CameraError('CONSTRAINT_ERROR', 'Camera could not satisfy the requested constraints.');
      }
    }
    throw new CameraError('NO_CAMERA', `Camera access failed: ${String(err)}`);
  }

  videoEl.srcObject = stream;
  // Mirror horizontally so user movement feels natural.
  videoEl.style.transform = 'scaleX(-1)';
  videoEl.playsInline = true;

  await videoEl.play();

  // Wait until the element actually delivers frames. Feeding a 0×0 video into
  // MediaPipe poisons its calculator graph permanently (RET_CHECK
  // `roi->width > 0`), killing hand tracking for the whole session — so we
  // must not resolve until real frame data exists.
  if (videoEl.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || videoEl.videoWidth === 0) {
    await new Promise<void>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | null = null;
      const cleanup = (): void => {
        if (timer !== null) clearTimeout(timer);
        videoEl.removeEventListener('loadeddata', onReady);
        videoEl.removeEventListener('error', onErr);
      };
      const onReady = (): void => {
        cleanup();
        resolve();
      };
      const onErr = (): void => {
        cleanup();
        reject(new CameraError('NO_CAMERA', 'Video element failed to deliver frames.'));
      };
      timer = setTimeout(() => {
        cleanup();
        reject(new CameraError('NO_CAMERA', 'Timed out waiting for the first camera frame.'));
      }, 10_000);
      videoEl.addEventListener('loadeddata', onReady);
      videoEl.addEventListener('error', onErr);
    });
  }

  return stream;
}

/**
 * Stops all tracks on the given stream and clears the video element's source,
 * releasing the camera device for other applications.
 */
export function stopCamera(stream: MediaStream): void {
  stream.getTracks().forEach((t) => t.stop());
}
